import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { formatGs, LOCATION_NAMES, formatDate } from "@/lib/format";
import { VoidBadge, VoidDecisionButtons } from "@/components/VoidActions";
import { toast } from "sonner";
import { Receipt, ChevronDown } from "lucide-react";

const PAY = { efectivo: "Efectivo", tarjeta: "POS", transferencia: "Transfer." };

const SaleRow = ({ s, onChanged }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-lg border ${s.status === "anulada" ? "border-destructive/40 opacity-70" : "border-border"}`} data-testid={`cashier-sale-${s.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-accent/50 rounded-lg" data-testid={`cashier-sale-toggle-${s.id}`}>
        <span className="text-xs text-muted-foreground w-32 whitespace-nowrap">{formatDate(s.timestamp)}</span>
        <span className="text-xs font-data text-muted-foreground w-20">#{s.id.slice(0, 8).toUpperCase()}</span>
        <span className="flex-1 truncate">{s.items.map((i) => `${i.quantity} ${i.name}`).join(", ")}</span>
        <VoidBadge sale={s} />
        <span className={`font-data font-bold ${s.status === "anulada" ? "line-through" : ""}`}>{formatGs(s.total)}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div className="space-y-1">
            {s.items.map((i, k) => <div key={k} className="flex justify-between"><span>{i.quantity} x {i.name}</span><span className="font-data">{formatGs(i.price * i.quantity)}</span></div>)}
            {s.discount > 0 && <div className="flex justify-between text-muted-foreground"><span>Descuento</span><span className="font-data">-{formatGs(s.discount)}</span></div>}
          </div>
          <div className="space-y-1">
            {Object.entries(s.payments || {}).map(([k, v]) => <div key={k} className="flex justify-between"><span>{PAY[k] || k}</span><span className="font-data">{formatGs(v)}</span></div>)}
            <div className="flex justify-between"><span>Vuelto</span><span className="font-data">{formatGs(s.change || 0)}</span></div>
            <p className="text-muted-foreground">{LOCATION_NAMES[s.location]} · Cajero: {s.cashier}</p>
            {s.void_status && (
              <div className="mt-2 p-2 rounded-md bg-secondary space-y-1">
                <p><b>Anulación:</b> {s.void_reason} <span className="text-muted-foreground">(pidió {s.void_requested_by}, {formatDate(s.void_requested_at)})</span></p>
                {s.void_decided_by && <p className="text-muted-foreground">{s.void_status === "aprobada" ? "Aprobó" : "Rechazó"} {s.void_decided_by}{s.void_note ? ` — ${s.void_note}` : ""}</p>}
                {s.void_status === "pendiente" && <VoidDecisionButtons saleId={s.id} onDone={onChanged} />}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const CashierSales = ({ cashierId, name, params, onChanged }) => {
  const [sales, setSales] = useState(null);
  const load = () => api.get("/sales", { params: { ...params, cashier: cashierId } }).then((r) => setSales(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [cashierId, JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = () => { load(); onChanged?.(); };
  const active = (sales || []).filter((s) => s.status !== "anulada");
  return (
    <div className="card-surface p-5 mb-6" data-testid="cashier-sales">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-head font-bold flex items-center gap-2"><Receipt className="w-4 h-4 text-primary" /> Ventas de {name}</h3>
        <span className="text-xs text-muted-foreground">{active.length} ventas · {formatGs(active.reduce((a, s) => a + s.total, 0))}{sales && sales.length > active.length ? ` · ${sales.length - active.length} anuladas` : ""}</span>
      </div>
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
        {sales === null && <p className="text-sm text-muted-foreground text-center py-4">Cargando…</p>}
        {sales?.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sin ventas en el período</p>}
        {sales?.map((s) => <SaleRow key={s.id} s={s} onChanged={changed} />)}
      </div>
    </div>
  );
};
